import { useState, useEffect } from 'react';
import api from '@/api/axios';
import { getERPEndpoints } from '@/utils/erpHelper';
import PageHeader from '@/components/PageHeader';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { 
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, 
  BarChart, Bar, Area, AreaChart, ComposedChart, Legend, ReferenceLine
} from 'recharts';
import { 
  TrendingUp, TrendingDown, DollarSign, FileText, CheckCircle2, ArrowRightLeft, 
  Wallet, Building2, AlertTriangle, AlertCircle, PiggyBank, Layers, RefreshCw,
  Filter, ArrowUpRight, ArrowDownRight, Minus, Calendar, Settings, Pencil
} from 'lucide-react';
import { toast } from 'sonner';

const CURRENCIES = [
  { value: 'MXN', label: 'MXN - Peso Mexicano', symbol: '$' },
  { value: 'USD', label: 'USD - Dólar USA', symbol: '$' },
  { value: 'EUR', label: 'EUR - Euro', symbol: '€' },
  { value: 'GBP', label: 'GBP - Libra Esterlina', symbol: '£' },
  { value: 'JPY', label: 'JPY - Yen Japonés', symbol: '¥' },
  { value: 'CHF', label: 'CHF - Franco Suizo', symbol: 'Fr' },
  { value: 'CAD', label: 'CAD - Dólar Canadiense', symbol: 'C$' },
  { value: 'CNY', label: 'CNY - Yuan Chino', symbol: '¥' }
];

const Dashboard = () => {
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [viewCurrency, setViewCurrency] = useState('MXN');
  const [selectedAccount, setSelectedAccount] = useState('all');
  const [bankAccounts, setBankAccounts] = useState([]);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [rangoActivo, setRangoActivo] = useState('13w'); // botón rápido activo; 'custom' si editan fechas a mano
  const [syncingRates, setSyncingRates] = useState(false);
  const [saldoBancosActual, setSaldoBancosActual] = useState(0); // saldo real desde bank-accounts/summary
  const [lastRateSync, setLastRateSync] = useState(null);
  const [schedulerStatus, setSchedulerStatus] = useState(null);
  const [fxAlerts, setFxAlerts] = useState(null);
  // Health check de datos de la empresa (fuente, conteos, alertas)
  const [health, setHealth] = useState(null);
  const loadHealth = async () => {
    try {
      const res = await api.get('/companies/health-check');
      setHealth(res.data);
    } catch { /* silencioso: el banner simplemente no se muestra */ }
  };
  const cambiarFuente = async (fuente) => {
    try {
      await api.post('/companies/fuente-datos', { fuente });
      toast.success('Fuente de datos actualizada — recalculando flujo');
      await loadHealth();
      loadDashboardData();
    } catch {
      toast.error('No se pudo cambiar la fuente de datos');
    }
  };
  useEffect(() => { loadHealth(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const [topClientesCxc, setTopClientesCxc] = useState([]); // top deudores del Aging CxC (MXN)
  const [agingCxc, setAgingCxc] = useState({ pctVencido: null, totalPendiente: 0, vencido: 0, bruto: 0, v30: 0, vmas60: 0 }); // resumen del Aging CxC
  // Saldo Inicial: 'auto' = saldo actual de cuentas bancarias (bank-accounts/summary) | 'manual' = capturado por el usuario
  const [saldoConfig, setSaldoConfig] = useState(() => {
    try {
      const saved = localStorage.getItem('dashboardSaldoInicial');
      if (saved) return JSON.parse(saved);
    } catch {/* ignore */}
    return { mode: 'auto', valor: 0 };
  });
  const loadSaldoConfig = async () => {
    try {
      const res = await api.get('/companies/cashflow-config');
      if (res.data && res.data.mode) {
        setSaldoConfig(res.data);
        localStorage.setItem('dashboardSaldoInicial', JSON.stringify(res.data));
      }
    } catch {
      // fallback: localStorage ya cargado en useState
    }
  };
  const [saldoDialogOpen, setSaldoDialogOpen] = useState(false);
  const [tempSaldoConfig, setTempSaldoConfig] = useState({ mode: 'auto', valor: 0 });
  
  const [scenarioConfig, setScenarioConfig] = useState(() => {
    const saved = localStorage.getItem('scenarioConfig');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        console.error('Error loading scenario config');
      }
    }
    return {
      pesimista: { cobranza: -30, gastos: 15 },
      optimista: { cobranza: 20, gastos: -10 }
    };
  });
  const [scenarioDialogOpen, setScenarioDialogOpen] = useState(false);
  const [tempScenarioConfig, setTempScenarioConfig] = useState(scenarioConfig);

  useEffect(() => {
    localStorage.setItem('scenarioConfig', JSON.stringify(scenarioConfig));
  }, [scenarioConfig]);

  const handleSaveScenarios = () => {
    setScenarioConfig(tempScenarioConfig);
    setScenarioDialogOpen(false);
    toast.success('Escenarios actualizados');
  };

  useEffect(() => {
    loadBankAccounts();
    loadSchedulerStatus();
    loadFxAlerts();
    loadTopClientesCxc();
    loadSaldoConfig();

    // Default = 13S: 13 semanas FUTURAS (hoy → hoy + 91 días)
    const today = new Date();
    const endDate = new Date(today);
    endDate.setDate(today.getDate() + 91);

    setDateFrom(today.toISOString().split('T')[0]);
    setDateTo(endDate.toISOString().split('T')[0]);
  }, []);

  useEffect(() => {
    if (dateFrom && dateTo) {
      loadDashboardData();
    }
  }, [viewCurrency, selectedAccount, dateFrom, dateTo, rangoActivo]);

  const loadBankAccounts = async () => {
    try {
      const response = await api.get('/bank-accounts');
      setBankAccounts(response.data);
    } catch (error) {
      console.error('Error loading bank accounts');
    }
  };

  const loadSchedulerStatus = async () => {
    try {
      const response = await api.get('/fx-rates/scheduler-status');
      setSchedulerStatus(response.data);
    } catch (error) {
      console.error('Error loading scheduler status');
    }
  };

  const loadFxAlerts = async () => {
    try {
      const response = await api.get('/fx-rates/alerts');
      setFxAlerts(response.data);
    } catch (error) {
      console.error('Error loading FX alerts');
    }
  };

  // Top clientes con mayor saldo pendiente del Aging CxC (caché de Contalink, montos MXN)
  const loadTopClientesCxc = async () => {
    try {
      const res = await api.get(getERPEndpoints().cxcEndpoint);
      const facturas = res.data?.facturas || [];
      const top = facturas
        .filter(f => (f.saldo_pendiente || 0) > 0)
        .sort((a, b) => (b.saldo_pendiente || 0) - (a.saldo_pendiente || 0))
        .slice(0, 5)
        .map(f => ({
          nombre: f.nombre || f.cliente_nombre || 'Sin nombre',
          monto: f.saldo_pendiente || 0
        }));
      setTopClientesCxc(top);
      // % vencido calculado desde el desglose bruto del aging: vencido ÷ (corriente + vencido).
      // El pct_vencido del backend divide entre el total NETO (con notas de crédito restadas),
      // lo que puede dar >100% — aquí ambos lados son brutos y el resultado queda acotado.
      const aging = res.data?.aging || {};
      const vencido = (aging.vencido_30 || 0) + (aging.vencido_60 || 0) +
                      (aging.vencido_90 || 0) + (aging.vencido_mas90 || 0);
      const bruto = (aging.corriente || 0) + vencido;
      const pctVencido = bruto > 0
        ? Math.min(100, (vencido / bruto) * 100)
        : (res.data?.pct_vencido ?? null);
      setAgingCxc({
        pctVencido,
        totalPendiente: res.data?.total_pendiente || 0,
        vencido,
        bruto,
        v30:    aging.vencido_30 || 0,
        vmas60: (aging.vencido_60 || 0) + (aging.vencido_90 || 0) + (aging.vencido_mas90 || 0),
      });
    } catch (error) {
      // No bloquea el dashboard, pero deja rastro para diagnóstico
      console.warn('loadTopClientesCxc: no se pudo cargar el Aging CxC —', error?.response?.status, error?.message);
    }
  };

  const syncFxRates = async () => {
    setSyncingRates(true);
    try {
      const response = await api.post('/fx-rates/sync');
      setLastRateSync(response.data);
      toast.success(`${response.data.message} desde Banxico y Open Exchange`);
      loadDashboardData();
      loadSchedulerStatus();
      loadFxAlerts();
    } catch (error) {
      toast.error('Error sincronizando tasas de cambio');
    } finally {
      setSyncingRates(false);
    }
  };

  const loadDashboardData = async () => {
    setLoading(true);
    try {
      let url = `/reports/dashboard-from-payments?moneda_vista=${viewCurrency}`;
      if (selectedAccount && selectedAccount !== 'all') {
        url += `&bank_account_id=${selectedAccount}`;
      }
      // El rango de fechas genera la ventana de semanas en el backend —
      // todos los gráficos y KPIs derivan de esas semanas.
      // En 13S se amplía la petición 8 semanas hacia atrás para que KPIs y
      // semáforos tengan historial real; el gráfico luego oculta esas semanas.
      let fechaInicioReq = dateFrom;
      if (rangoActivo === '13w' && dateFrom) {
        const d = new Date(dateFrom + 'T00:00:00');
        d.setDate(d.getDate() - 56); // 8 semanas de historial
        fechaInicioReq = d.toISOString().split('T')[0];
      }
      if (fechaInicioReq) url += `&fecha_inicio=${fechaInicioReq}`;
      if (dateTo) url += `&fecha_fin=${dateTo}`;

      const [response, summaryRes] = await Promise.all([
        api.get(url),
        api.get(`/bank-accounts/summary${dateFrom ? `?fecha=${dateFrom}` : ''}`),
      ]);

      const data = response.data;
      const weeks = data?.weeks || [];
      const summaryData = summaryRes.data || {};
      const summaryTotalMxn = summaryData?.total_mxn || 0;
      setSaldoBancosActual(summaryTotalMxn);

      // ── Cash Pool y Detalle de Cuentas desde /bank-accounts/summary ──────
      // El endpoint dashboard-from-payments calcula saldos desde pagos históricos
      // y mezcla traspasos/comisiones que distorsionan los totales.
      // /bank-accounts/summary usa las anclas históricas verificadas (MongoDB history)
      // y es la fuente de verdad para saldos reales. Lo sobreescribimos aquí.
      const cashPoolFromSummary = {};
      const accountsFromSummary = [];
      const porMoneda = summaryData?.por_moneda || {};
      const porBanco = summaryData?.por_banco || {};

      // Cash pool por moneda (en moneda original, como el backend devolvía)
      Object.entries(porMoneda).forEach(([moneda, info]) => {
        cashPoolFromSummary[moneda] = { total: info.saldo || 0, cuentas: info.cuentas || 0 };
      });

      // Detalle de cuentas (formato compatible con lo que usa el JSX)
      Object.entries(porBanco).forEach(([banco, bancoInfo]) => {
        (bancoInfo.cuentas || []).forEach(ct => {
          accountsFromSummary.push({
            id: ct.id,
            nombre: ct.nombre,
            banco,
            moneda: ct.moneda,
            saldo_inicial: ct.saldo,
            saldo_final: ct.saldo,      // sin movimientos — saldo verificado del corte
            saldo_inicial_mxn: ct.saldo_mxn,
            saldo_final_mxn: ct.saldo_mxn,
            num_movimientos: 0,
            riesgo: ct.saldo_mxn > 50000 ? 'bajo' : ct.saldo_mxn > 10000 ? 'medio' : 'alto',
          });
        });
      });

      setDashboardData({
        ...data,
        cash_pool: cashPoolFromSummary,
        bank_accounts: accountsFromSummary,
      });

      console.log('[Dashboard diagnóstico]', {
        // Banco base
        bank_summary_total_mxn:   summaryTotalMxn,           // /bank-accounts/summary (= CashFlow base)
        saldo_bancos_dashboard:   data?.saldo_bancos,         // dashboard endpoint (puede filtrar por cuenta)
        saldo_actual_calculado:   data?.saldo_actual,         // bank_base + all_payments net
        fecha_saldo_bancos:       data?.fecha_saldo_bancos,
        // Discrepancia esperada: bank_summary_total_mxn ≈ saldo_actual_calculado - payment_net
        payment_net_estimado:     (data?.saldo_actual || 0) - (data?.saldo_bancos || 0),
        // Gráfico
        saldo_proyectado:         data?.saldo_proyectado,
        semanas:                  weeks.length,
        primera_semana:           weeks[0] ? `${weeks[0].abs_semana} saldo_inicial=${weeks[0].saldo_inicial}` : null,
        // Cuentas bancarias del summary (expande para ver fecha_saldo de cada una)
        summary_por_banco:        summaryRes.data?.por_banco,
        summary_por_moneda:       summaryRes.data?.por_moneda,
      });
    } catch (error) {
      toast.error('Error cargando dashboard');
    } finally {
      setLoading(false);
    }
  };

  const setQuickDateRange = (range) => {
    const today = new Date();
    const from = new Date(today);
    const to = new Date(today);

    switch(range) {
      // Históricos: hacia atrás desde hoy
      case '1w': from.setDate(today.getDate() - 7); break;
      case '1m': from.setMonth(today.getMonth() - 1); break;
      case '3m': from.setMonth(today.getMonth() - 3); break;
      case '6m': from.setMonth(today.getMonth() - 6); break;
      case '1y': from.setFullYear(today.getFullYear() - 1); break;
      // 13S (default): 13 semanas FUTURAS — hoy → hoy + 91 días
      case '13w':
      default:
        to.setDate(today.getDate() + 91);
        break;
    }

    setDateFrom(from.toISOString().split('T')[0]);
    setDateTo(to.toISOString().split('T')[0]);
    setRangoActivo(range);
  };

  const formatCurrency = (amount) => {
    const curr = CURRENCIES.find(c => c.value === viewCurrency) || CURRENCIES[0];
    return `${curr.symbol}${amount.toLocaleString('es-MX', {minimumFractionDigits: 2, maximumFractionDigits: 2})}`;
  };

  // "03 nov - 09 nov" para una semana de chartData (fecha_fin del backend es exclusiva)
  const formatWeekRange = (week) => {
    if (!week?.fecha_inicio) return '';
    const ini = new Date(week.fecha_inicio);
    const fin = week.fecha_fin
      ? new Date(new Date(week.fecha_fin).getTime() - 86400000)
      : new Date(ini.getTime() + 6 * 86400000);
    const f = (d) => d.toLocaleDateString('es-MX', { day: '2-digit', month: 'short' });
    return `${f(ini)} - ${f(fin)}`;
  };

  const handleSaveSaldoConfig = async () => {
    const cfg = { mode: tempSaldoConfig.mode, valor: Number(tempSaldoConfig.valor) || 0 };
    setSaldoConfig(cfg);
    localStorage.setItem('dashboardSaldoInicial', JSON.stringify(cfg));
    setSaldoDialogOpen(false);
    try {
      await api.post('/companies/cashflow-config', cfg);
    } catch {
      // silencioso — localStorage ya tiene el valor
    }
    toast.success(cfg.mode === 'auto'
      ? 'Saldo Inicial: automático (saldo actual de cuentas bancarias)'
      : `Saldo Inicial manual: ${formatCurrency(cfg.valor)}`);
  };

  if (loading && !dashboardData) {
    return <div className="p-8">Cargando dashboard...</div>;
  }

  // saldo_actual: apertura bancaria + todos los pagos completados hasta hoy (ALL, sin filtro
  //   de conciliación). Misma lógica que CashFlow → debe coincidir con saldo_final S_actual.
  // saldo_bancos: saldo de apertura estático (base del gráfico semanal del backend).
  // saldoDelta: desplaza los saldos del gráfico para que arranquen desde saldo_actual.
  const fxRateDisplay = dashboardData?.fx_rate || 1;
  const saldoActualDisplay = dashboardData?.saldo_actual || 0;   // ya en moneda de visualización
  const saldoBackendS1 = dashboardData?.saldo_bancos || 0;       // base del gráfico (apertura)
  const saldoBancosActualDisplay = fxRateDisplay > 0 ? saldoBancosActual / fxRateDisplay : saldoBancosActual;
  const saldoInicial = saldoConfig.mode === 'manual' ? (Number(saldoConfig.valor) || 0) : saldoBancosActual;
  // delta ≠ 0 en modo auto cuando saldo_actual > saldo_bancos (e.g. $8M - $2M = $6M)
  const saldoDelta = saldoInicial - saldoBackendS1;

  // Aviso de saldo desactualizado: si la fecha_saldo más antigua supera 30 días
  const fechaSaldoBancos = dashboardData?.fecha_saldo_bancos;
  const diasDesdeSaldo = fechaSaldoBancos
    ? Math.floor((Date.now() - new Date(fechaSaldoBancos).getTime()) / (1000 * 60 * 60 * 24))
    : null;
  const saldoDesactualizado = diasDesdeSaldo !== null && diasDesdeSaldo > 30;

  const chartData = (dashboardData?.weeks || []).map((week, idx) => ({
    semana: week.week_label || `S${idx + 1}`,
    abs_semana: week.abs_semana || `S${idx + 1}`,
    date_label: week.date_label || '',
    fecha_inicio: week.fecha_inicio || null,
    fecha_fin: week.fecha_fin || null,
    ingresos: week.ingresos_display ?? week.ingresos ?? week.total_ingresos ?? 0,
    egresos: week.egresos_display ?? week.egresos ?? week.total_egresos ?? 0,
    flujo_neto: week.flujo_neto_display ?? week.flujo_neto ?? 0,
    saldo_inicial: (week.saldo_inicial_display ?? week.saldo_inicial ?? 0) + saldoDelta,
    saldo_final: (week.saldo_final_display ?? week.saldo_final ?? 0) + saldoDelta,
    venta_usd: week.venta_usd || 0,
    compra_usd: week.compra_usd || 0,
    num_payments: week.num_payments || 0,
    varianza: week.varianza_display ?? week.varianza ?? week.flujo_neto ?? 0,
    varianza_pct: week.varianza_pct || 0,
    // El nuevo endpoint usa is_past/is_current; el legacy usaba es_real
    is_past:    week.is_past    ?? week.es_real   ?? false,
    is_current: week.is_current ?? false,
    es_real:    week.is_past    ?? week.es_real   ?? false,
    es_current: week.is_current ?? false,
    saldo_anclado: week.saldo_anclado ?? false,
  }));

  // displayChartData: mostrar TODAS las semanas (pasadas + futuras)
  // El filtro anterior solo dejaba futuras → SF proyectado = SI (bug)
  const displayChartData = chartData;

  const kpis = dashboardData?.kpis || {};

  // Saldo Final Proyectado = saldo_final de la última semana del periodo
  const lastWeek = displayChartData[displayChartData.length - 1];
  const saldoFinalProyectado = lastWeek?.saldo_final ?? saldoInicial;

  // Label dinámico: abs_semana de la última semana proyectada visible
  const lastDisplayWeek = displayChartData[displayChartData.length - 1];
  const lastWeekLabel = lastDisplayWeek?.abs_semana || 'S?';
  const burnRate = dashboardData?.burn_rate || 0;
  const runwayWeeks = dashboardData?.runway_weeks;
  const criticalWeek = dashboardData?.critical_week;
  const filteredAccount = dashboardData?.filtered_account;
  const displayCurrency = dashboardData?.moneda_vista || viewCurrency;
  const cobranzaVsPagos = dashboardData?.cobranza_vs_pagos || 100;
  const totalIngresos = dashboardData?.total_ingresos || 0;
  const totalEgresos = dashboardData?.total_egresos || 0;
  
  const recentWeeks = chartData.filter(w => (w.es_real || w.es_current)).slice(-4);
  const trend = recentWeeks.length >= 2 ? {
    direction: recentWeeks[recentWeeks.length - 1].flujo_neto > recentWeeks[0].flujo_neto ? 'up' : 
               recentWeeks[recentWeeks.length - 1].flujo_neto < recentWeeks[0].flujo_neto ? 'down' : 'stable',
    avg_flow_4w: recentWeeks.reduce((sum, w) => sum + w.flujo_neto, 0) / recentWeeks.length
  } : { direction: 'stable', avg_flow_4w: 0 };
  
  const risks = {
    liquidez_critica: saldoFinalProyectado < 50000,
    tendencia_negativa: trend.direction === 'down' && trend.avg_flow_4w < 0,
    semanas_con_deficit: chartData.filter(w => w.saldo_final < 0).length
  };
  const accounts = dashboardData?.bank_accounts || [];
  const cashPool = dashboardData?.cash_pool || {};

  // ── Datos para ResumenEjecutivoIA ────────────────────────────────────────
  // Toma los datos reales de flujo del dashboard y los mapea al formato del componente.
  // Los campos de P&L (costoVentas, utilidadBruta, gastosOp) son estimaciones proxy
  // ─────────────────────────────────────────────────────────────────────────

  const TrendIcon = trend.direction === 'up' ? ArrowUpRight : trend.direction === 'down' ? ArrowDownRight : Minus;
  const trendColor = trend.direction === 'up' ? 'text-green-600' : trend.direction === 'down' ? 'text-red-600' : 'text-gray-500';

  const hasRiskAlerts = risks.liquidez_critica || risks.tendencia_negativa || risks.semanas_con_deficit > 0;
  const hasFxAlerts = fxAlerts?.has_alerts;

  // ── Semáforo de Decisiones ─────────────────────────────────────────────
  // Estado verde/amarillo/rojo de las áreas clave. Todos los cálculos sobre
  // semanas con datos reales (is_past/is_current); 'neutral' = sin datos.
  const semaforos = (() => {
    const reales = chartData.filter(w => (w.es_real || w.es_current));
    const items = [];

    // 1. LIQUIDEZ — runway con BURN NETO (egresos − ingresos), no bruto.
    // FIX 2026-07-05: la fórmula anterior dividía el saldo entre los egresos
    // brutos, asumiendo que los ingresos caen a cero — daba "0-1 semanas" de
    // pánico en operaciones sanas donde entra tanto como sale. El runway real
    // se mide contra lo que la caja CONSUME neto cada semana.
    const saldoActual = reales.length > 0 ? reales[reales.length - 1].saldo_final : saldoInicial;
    const ingProm = reales.length > 0
      ? reales.reduce((s, w) => s + (w.ingresos || 0), 0) / reales.length : 0;
    const egrProm = reales.length > 0
      ? reales.reduce((s, w) => s + (w.egresos || 0), 0) / reales.length : 0;
    const burnNeto = egrProm - ingProm;   // >0 = la caja se consume; ≤0 = genera
    const runway = reales.length === 0 ? null
      : burnNeto <= 0 ? Infinity
      : Math.max(0, saldoActual) / burnNeto;
    items.push({
      nombre: 'Liquidez',
      estado: runway === null ? 'neutral'
        : runway === Infinity ? 'verde'
        : runway > 12 ? 'verde' : runway >= 6 ? 'amarillo' : 'rojo',
      valor: runway === null ? '—'
        : runway === Infinity ? 'Generando caja'
        : `${Math.round(runway)} semanas`,
      explicacion: runway === null ? 'Sin semanas reales para calcular runway'
        : runway === Infinity ? 'Los ingresos cubren los egresos: la caja crece en promedio'
        : runway > 12 ? 'Caja para más de 3 meses al ritmo neto actual'
        : runway >= 6 ? 'Caja justa: vigila cobranza y pagos grandes'
        : 'Riesgo de caja: requiere acciones inmediatas',
      composicion: burnNeto <= 0
        ? `Saldo ${formatCurrency(saldoActual)} · flujo neto +${formatCurrency(Math.abs(burnNeto))}/sem (ing ${formatCurrency(ingProm)} vs egr ${formatCurrency(egrProm)})`
        : `Saldo ${formatCurrency(saldoActual)} ÷ burn neto ${formatCurrency(burnNeto)}/sem (egr ${formatCurrency(egrProm)} − ing ${formatCurrency(ingProm)})`,
    });

    // 2. COBRANZA — ratio ingresos/egresos de las semanas reales
    const ingReal = reales.reduce((s, w) => s + (w.ingresos || 0), 0);
    const egrReal = reales.reduce((s, w) => s + (w.egresos || 0), 0);
    const ratio = egrReal > 0 ? (ingReal / egrReal) * 100 : null;
    items.push({
      nombre: 'Cobranza',
      estado: ratio === null ? 'neutral' : ratio > 80 ? 'verde' : ratio >= 60 ? 'amarillo' : 'rojo',
      valor: ratio === null ? '—' : `${ratio.toFixed(0)}%`,
      explicacion: ratio === null ? 'Sin egresos para comparar'
        : ratio > 80 ? 'Lo cobrado cubre los pagos del período'
        : ratio >= 60 ? 'Cobranza por debajo de los pagos: dar seguimiento'
        : 'Cobranza muy por debajo de los pagos: intensificar gestión',
      composicion: `Cobrado ${formatCurrency(ingReal)} ÷ pagado ${formatCurrency(egrReal)} (sem. reales)`,
    });

    // 3. EGRESOS — burn rate últimas 4 semanas reales vs las 4 anteriores
    const ult4 = reales.slice(-4);
    const prev4 = reales.slice(-8, -4);
    const burnAct = ult4.length > 0 ? ult4.reduce((s, w) => s + (w.egresos || 0), 0) / ult4.length : 0;
    const burnPrev = prev4.length > 0 ? prev4.reduce((s, w) => s + (w.egresos || 0), 0) / prev4.length : 0;
    const deltaPct = burnPrev > 0 ? ((burnAct - burnPrev) / burnPrev) * 100 : null;
    items.push({
      nombre: 'Egresos',
      estado: deltaPct === null ? 'neutral' : deltaPct <= 0 ? 'verde' : deltaPct < 15 ? 'amarillo' : 'rojo',
      valor: deltaPct === null
        ? `${formatCurrency(burnAct)}/sem`
        : `${deltaPct >= 0 ? '+' : ''}${deltaPct.toFixed(0)}% vs mes anterior`,
      explicacion: deltaPct === null ? 'Sin mes anterior completo para comparar'
        : deltaPct <= 0 ? `Burn rate bajó: ${formatCurrency(burnAct)}/sem actual`
        : deltaPct < 15 ? `Burn rate subió moderado: ${formatCurrency(burnAct)}/sem actual`
        : `Burn rate disparado: ${formatCurrency(burnAct)}/sem actual`,
      composicion: deltaPct === null
        ? `Últimas 4 sem: ${formatCurrency(burnAct)}/sem (sin 4 previas)`
        : `Últimas 4 sem ${formatCurrency(burnAct)}/sem vs previas ${formatCurrency(burnPrev)}/sem`,
    });

    // 4. CARTERA VENCIDA — pct_vencido del Aging CxC
    const pct = agingCxc.pctVencido;
    items.push({
      nombre: 'Cartera Vencida',
      estado: pct == null ? 'neutral' : pct < 30 ? 'verde' : pct <= 60 ? 'amarillo' : 'rojo',
      valor: pct == null ? '—' : `${pct.toFixed(0)}%`,
      explicacion: pct == null ? 'Sin datos del Aging CxC (sube el Excel de Contalink)'
        : pct < 30 ? 'Cartera sana: la mayoría al corriente'
        : pct <= 60 ? 'Cartera deteriorándose: priorizar vencidos'
        : 'Cartera crítica: la mayoría del saldo está vencido',
      composicion: pct == null ? 'Fuente: Aging CxC de Contalink'
        : agingCxc.vencido > 0 && agingCxc.bruto === agingCxc.vencido
          ? (() => {
              const p30    = Math.round(agingCxc.v30    / agingCxc.vencido * 100);
              const pmas60 = Math.round(agingCxc.vmas60 / agingCxc.vencido * 100);
              return `${p30}% reciente (1-30d) · ${pmas60}% crítico (+60d)`;
            })()
          : `Vencido $${agingCxc.vencido.toLocaleString('es-MX', {maximumFractionDigits: 0})} ÷ ` +
            `cartera bruta $${agingCxc.bruto.toLocaleString('es-MX', {maximumFractionDigits: 0})} MXN`,
    });

    return items;
  })();

  return (
    <div className="p-6 space-y-6 bg-[#F8FAFC] min-h-screen" data-testid="dashboard-page">
      {/* FX Rate Alerts Banner */}
      {hasFxAlerts && (
        <div className={`rounded-lg border-l-4 p-4 ${fxAlerts.critical_count > 0 ? 'bg-red-50 border-red-500' : 'bg-amber-50 border-amber-500'}`} data-testid="fx-alerts-banner">
          <div className="flex items-start gap-3">
            <AlertTriangle className={`w-5 h-5 mt-0.5 ${fxAlerts.critical_count > 0 ? 'text-red-600' : 'text-amber-600'}`} />
            <div className="flex-1">
              <h3 className={`font-semibold ${fxAlerts.critical_count > 0 ? 'text-red-800' : 'text-amber-800'}`}>
                {fxAlerts.critical_count > 0 ? '⚠️ Alerta Crítica de Tipo de Cambio' : '📊 Variación en Tipos de Cambio'}
              </h3>
              <div className="mt-2 space-y-1">
                {fxAlerts.alerts.map((alert, idx) => (
                  <div key={idx} className={`text-sm flex items-center gap-2 ${alert.type === 'critical' ? 'text-red-700 font-medium' : 'text-amber-700'}`}>
                    {alert.direction === 'subió' ? (
                      <TrendingUp size={14} className="text-red-500" />
                    ) : (
                      <TrendingDown size={14} className="text-green-500" />
                    )}
                    <span>{alert.message}</span>
                    <span className="text-xs text-gray-500">({alert.source})</span>
                  </div>
                ))}
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Umbral de alerta: variación mayor al {fxAlerts.threshold_percent}% respecto al día anterior
              </p>
            </div>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => setFxAlerts(null)}
              className="text-gray-400 hover:text-gray-600"
            >
              ✕
            </Button>
          </div>
        </div>
      )}

      {/* Header — banner Pulse de ancho completo con acciones integradas */}
      <div className="flex flex-col gap-4">
        <PageHeader
          title="Dashboard Ejecutivo"
          subtitle="Flujo de efectivo · 13 semanas rolling"
          actions={
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={syncFxRates}
                disabled={syncingRates}
                className="gap-2 bg-white/10 border-white/25 text-white hover:bg-white/20 hover:text-white"
                data-testid="sync-rates-btn"
              >
                <DollarSign size={14} className={syncingRates ? 'animate-spin' : ''} />
                {syncingRates ? 'Sincronizando...' : 'Actualizar Tasas'}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={loadDashboardData}
                className="gap-2 bg-white/10 border-white/25 text-white hover:bg-white/20 hover:text-white"
              >
                <RefreshCw size={14} />
                Actualizar
              </Button>
            </>
          }
        />
        
        {/* ── Health check: alertas de datos + selector de fuente ── */}
        {health && (health.alertas || []).length > 0 && (
          <div className={`rounded-xl border px-4 py-3 ${health.ok ? 'bg-amber-50 border-amber-200' : 'bg-red-50 border-red-200'}`}
               data-testid="health-banner">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-1.5">
              <div className={`text-sm font-bold ${health.ok ? 'text-amber-800' : 'text-red-800'}`}>
                {health.ok ? '⚠️ Revisa la configuración de datos de esta empresa' : '⛔ Hay problemas con los datos de esta empresa'}
              </div>
              <div className="flex items-center gap-2 text-xs">
                <span className="text-slate-500 font-medium">Fuente de datos:</span>
                <select
                  value={health.fuente_configurada || 'auto'}
                  onChange={(e) => cambiarFuente(e.target.value)}
                  className="border border-slate-300 rounded-md px-2 py-1 text-xs bg-white"
                  data-testid="fuente-datos-select"
                >
                  <option value="auto">Auto — detectada: {(health.fuente_efectiva || '').toUpperCase()}</option>
                  <option value="sat">SAT (CFDIs)</option>
                  <option value="alegra">Alegra</option>
                  <option value="contalink">Contalink</option>
                </select>
              </div>
            </div>
            <ul className="space-y-1">
              {health.alertas.map((a, i) => (
                <li key={a.codigo || i}
                    className={`text-xs flex items-start gap-1.5 ${
                      a.nivel === 'error' ? 'text-red-700' : a.nivel === 'warning' ? 'text-amber-700' : 'text-slate-500'}`}>
                  <span className="shrink-0">{a.nivel === 'error' ? '⛔' : a.nivel === 'warning' ? '⚠️' : 'ℹ️'}</span>
                  <span>{a.mensaje}</span>
                </li>
              ))}
            </ul>
            <div className="text-[11px] text-slate-400 mt-1.5">
              Fuente efectiva: <b>{health.fuente_efectiva}</b> ({health.criterio}) ·
              CFDIs: {health.conteos?.cfdis_total ?? 0} · Movs. Alegra: {health.conteos?.bank_transactions_alegra ?? 0} ·
              Cuentas: {health.conteos?.bank_accounts ?? 0}
              {health.rango_cfdis?.hasta ? ` · Último CFDI: ${health.rango_cfdis.hasta}` : ''}
            </div>
          </div>
        )}

        <div className="flex flex-wrap items-center gap-3">
          {schedulerStatus?.scheduler?.running && (
            <div className="flex items-center gap-2 text-xs text-blue-700 bg-blue-50 rounded-md px-3 py-1.5">
              <div className="w-2 h-2 bg-blue-500 rounded-full animate-pulse" />
              <span className="font-medium">Auto-sync activo</span>
              <span className="text-blue-500">|</span>
              <span>Próxima: {schedulerStatus.scheduler.jobs?.[0]?.next_run_formatted?.split(' ').slice(1, 3).join(' ')}</span>
            </div>
          )}
          {lastRateSync && (
            <div className="flex items-center gap-2 text-xs text-green-700 bg-green-50 rounded-md px-3 py-1.5">
              <CheckCircle2 size={12} />
              Tasas: {lastRateSync.rates?.map(r => `${r.moneda}: ${r.tasa_mxn.toFixed(2)}`).join(' | ')}
            </div>
          )}
        </div>
        
        {/* Filters Row */}
        <div className="flex flex-wrap items-center gap-3 p-3 bg-white rounded-lg border shadow-sm">
          <span className="text-sm font-medium text-[#64748B] flex items-center gap-1">
            <Filter size={14} /> Filtros:
          </span>
          <div className="flex items-center gap-2 bg-[#F8FAFC] rounded-md px-3 py-1.5 border">
            <DollarSign size={14} className="text-[#64748B]" />
            <Select value={viewCurrency} onValueChange={setViewCurrency}>
              <SelectTrigger className="w-44 border-0 bg-transparent p-0 h-7 focus:ring-0" data-testid="currency-selector">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CURRENCIES.map(c => (
                  <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2 bg-[#F8FAFC] rounded-md px-3 py-1.5 border">
            <Building2 size={14} className="text-[#64748B]" />
            <Select value={selectedAccount} onValueChange={setSelectedAccount}>
              <SelectTrigger className="w-40 border-0 bg-transparent p-0 h-7 focus:ring-0" data-testid="account-filter">
                <SelectValue placeholder="Todas las cuentas" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas las cuentas</SelectItem>
                {bankAccounts.map(acc => (
                  <SelectItem key={acc.id} value={acc.id}>{acc.nombre}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2 bg-[#F8FAFC] rounded-md px-3 py-1.5 border">
            <Calendar size={14} className="text-[#64748B]" />
            <Input 
              type="date" value={dateFrom} onChange={(e) => { setDateFrom(e.target.value); setRangoActivo('custom'); }}
              className="w-32 h-7 border-0 bg-transparent p-0 text-sm" data-testid="date-from"
            />
            <span className="text-[#64748B] text-sm">-</span>
            <Input 
              type="date" value={dateTo} onChange={(e) => { setDateTo(e.target.value); setRangoActivo('custom'); }}
              className="w-32 h-7 border-0 bg-transparent p-0 text-sm" data-testid="date-to"
            />
          </div>
          <div className="flex items-center gap-1">
            {[['1w', '1S'], ['1m', '1M'], ['3m', '3M'], ['6m', '6M'], ['1y', '1A'], ['13w', '13S']].map(([r, label]) => (
              <Button
                key={r}
                variant="ghost"
                size="sm"
                onClick={() => setQuickDateRange(r)}
                className={`h-7 px-2 text-xs ${rangoActivo === r ? 'bg-[#E0F2FE] font-semibold text-blue-700' : ''}`}
                data-testid={`quick-range-${r}`}
              >
                {label}
              </Button>
            ))}
          </div>
        </div>
      </div>

      {/* Risk Alerts Banner */}
      {hasRiskAlerts && (
        <Card className="border-amber-300 bg-amber-50">
          <CardContent className="py-3">
            <div className="flex items-center gap-3">
              <AlertTriangle className="h-5 w-5 text-amber-600" />
              <div className="flex-1 flex flex-wrap gap-4 text-sm">
                {risks.liquidez_critica && (
                  <span className="flex items-center gap-1 text-red-700">
                    <AlertCircle size={14} /> Liquidez crítica proyectada
                  </span>
                )}
                {risks.tendencia_negativa && (
                  <span className="flex items-center gap-1 text-red-700">
                    <TrendingDown size={14} /> Tendencia negativa
                  </span>
                )}
                {risks.saldos_ociosos > 0 && (
                  <span className="flex items-center gap-1 text-amber-700">
                    <PiggyBank size={14} /> {risks.saldos_ociosos} cuenta(s) con saldo ocioso
                  </span>
                )}
                {risks.cuentas_bajo_saldo > 0 && (
                  <span className="flex items-center gap-1 text-amber-700">
                    <AlertTriangle size={14} /> {risks.cuentas_bajo_saldo} cuenta(s) con bajo saldo
                  </span>
                )}
                {risks.semanas_con_deficit > 0 && (
                  <span className="flex items-center gap-1 text-amber-700">
                    <AlertCircle size={14} /> {risks.semanas_con_deficit} semana(s) con déficit
                  </span>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Account Filter Info */}
      {filteredAccount && (
        <Card className="border-l-4 border-l-purple-500 shadow-sm bg-purple-50/50">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-purple-700 flex items-center gap-2">
              <Building2 size={16} />
              Cuenta Seleccionada: {filteredAccount.nombre}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <p className="text-xs text-purple-600">Banco</p>
                <p className="font-medium">{filteredAccount.banco || '-'}</p>
              </div>
              <div>
                <p className="text-xs text-purple-600">Moneda</p>
                <p className="font-medium">{filteredAccount.moneda}</p>
              </div>
              <div>
                <p className="text-xs text-purple-600">Saldo Inicial ({filteredAccount.moneda})</p>
                <p className="font-bold mono text-lg">${filteredAccount.saldo_inicial?.toLocaleString('es-MX', {minimumFractionDigits: 2})}</p>
              </div>
              <div>
                <p className="text-xs text-purple-600">Movimientos</p>
                <p className="font-medium">{filteredAccount.num_movements} pagos</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Main KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* KPI héroe — tema Pulse (gradiente indigo, firma visual de la marca) */}
        <Card className="border-0 bg-brand-gradient text-white shadow-card-lg">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-white/70 flex items-center justify-between">
              <span className="flex items-center gap-1.5">
                Saldo Inicial
                <button
                  onClick={() => {
                    setTempSaldoConfig({ mode: saldoConfig.mode, valor: saldoConfig.valor || saldoBackendS1 });
                    setSaldoDialogOpen(true);
                  }}
                  className="text-white/50 hover:text-white transition-colors"
                  title="Configurar saldo inicial"
                  data-testid="edit-saldo-inicial"
                >
                  <Pencil size={12} />
                </button>
              </span>
              <button
                onClick={() => {
                  const cfg = { mode: 'auto', valor: 0 };
                  setSaldoConfig(cfg);
                  localStorage.setItem('dashboardSaldoInicial', JSON.stringify(cfg));
                }}
                className="text-white/50 hover:text-white transition-colors"
                title="Usar saldo real de cuentas bancarias"
                data-testid="saldo-desde-bancos-btn"
              >
                <Building2 className="h-4 w-4" />
              </button>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold mono text-white">
              {formatCurrency(saldoInicial)}
            </div>
            <p className="text-xs text-white/60 mt-1">
              {saldoConfig.mode === 'manual' ? 'Manual · ' : ''}
              {filteredAccount ? `${filteredAccount.nombre}` : `Consolidado en ${displayCurrency}`}
            </p>
            {saldoDesactualizado && (
              <p className="text-xs text-amber-200 mt-1.5 flex items-center gap-1">
                ⚠️ Saldo al {fechaSaldoBancos ? new Date(fechaSaldoBancos).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'} · Actualiza en Cuentas y Bancos
              </p>
            )}
          </CardContent>
        </Card>

        {/* Modal de configuración del Saldo Inicial */}
        <Dialog open={saldoDialogOpen} onOpenChange={setSaldoDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Configurar Saldo Inicial</DialogTitle>
            </DialogHeader>
            <div className="space-y-3 py-2">
              <label className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                tempSaldoConfig.mode === 'auto' ? 'border-blue-400 bg-blue-50' : 'border-gray-200 hover:border-gray-300'
              }`}>
                <input
                  type="radio"
                  name="saldoMode"
                  checked={tempSaldoConfig.mode === 'auto'}
                  onChange={() => setTempSaldoConfig(prev => ({ ...prev, mode: 'auto' }))}
                  className="mt-1"
                  data-testid="saldo-mode-auto"
                />
                <div>
                  <div className="font-medium text-sm">
                    Automático — saldo calculado ({formatCurrency(saldoActualDisplay)})
                  </div>
                  <div className="text-xs text-gray-500 mt-0.5">
                    Apertura bancaria + todos los pagos conciliados · misma fuente que CashFlow Projections
                  </div>
                </div>
              </label>
              <label className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                tempSaldoConfig.mode === 'manual' ? 'border-blue-400 bg-blue-50' : 'border-gray-200 hover:border-gray-300'
              }`}>
                <input
                  type="radio"
                  name="saldoMode"
                  checked={tempSaldoConfig.mode === 'manual'}
                  onChange={() => setTempSaldoConfig(prev => ({ ...prev, mode: 'manual' }))}
                  className="mt-1"
                  data-testid="saldo-mode-manual"
                />
                <div className="flex-1">
                  <div className="font-medium text-sm">Ingresar manualmente</div>
                  <Input
                    type="number"
                    step="0.01"
                    value={tempSaldoConfig.valor}
                    disabled={tempSaldoConfig.mode !== 'manual'}
                    onChange={(e) => setTempSaldoConfig(prev => ({ ...prev, valor: e.target.value }))}
                    className="h-8 mt-2"
                    placeholder="0.00"
                    data-testid="saldo-manual-input"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    En {displayCurrency} · se guarda en este navegador
                  </p>
                </div>
              </label>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setSaldoDialogOpen(false)}>Cancelar</Button>
              <Button onClick={handleSaveSaldoConfig} className="bg-[#0F172A]" data-testid="saldo-save-btn">Guardar</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Card className="border-l-4 border-l-blue-500 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-[#64748B] flex items-center justify-between">
              <span>Saldo Final Proyectado ({lastWeekLabel})</span>
              <Wallet className="h-4 w-4" />
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className={`text-2xl font-bold mono ${saldoFinalProyectado >= 0 ? 'text-[#0F172A]' : 'text-red-600'}`}>
              {formatCurrency(saldoFinalProyectado)}
            </div>
            <div className="flex items-center gap-1 mt-1">
              <TrendIcon size={14} className={saldoFinalProyectado < 0 ? 'text-red-600' : trendColor} />
              <span className={`text-xs ${saldoFinalProyectado < 0 ? 'text-red-600' : trendColor}`}>
                {saldoFinalProyectado < 0
                  ? 'Déficit proyectado'
                  : `Tendencia ${trend.direction === 'up' ? 'positiva' : trend.direction === 'down' ? 'negativa' : 'estable'}`}
              </span>
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-purple-500 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-[#64748B] flex items-center justify-between">
              <span>Flujo Promedio (4 sem)</span>
              <TrendingUp className="h-4 w-4" />
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className={`text-2xl font-bold mono ${trend.avg_flow_4w >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {trend.avg_flow_4w >= 0 ? '+' : ''}{formatCurrency(trend.avg_flow_4w)}
            </div>
            <p className="text-xs text-[#94A3B8] mt-1">por semana</p>
          </CardContent>
        </Card>
      </div>

      {/* Semáforo de Decisiones */}
      <Card className="shadow-sm" data-testid="semaforo-decisiones">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <AlertCircle size={16} className="text-[#6366F1]" />
            Semáforo de Decisiones
          </CardTitle>
          <CardDescription>Estado actual de las áreas clave del negocio</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {semaforos.map((s) => {
              const colores = {
                verde:    { dot: 'bg-green-500', bg: 'bg-green-50 border-green-200', text: 'text-green-700' },
                amarillo: { dot: 'bg-amber-400', bg: 'bg-amber-50 border-amber-200', text: 'text-amber-700' },
                rojo:     { dot: 'bg-red-500',   bg: 'bg-red-50 border-red-200',     text: 'text-red-700' },
                neutral:  { dot: 'bg-gray-300',  bg: 'bg-gray-50 border-gray-200',   text: 'text-gray-500' },
              }[s.estado];
              return (
                <div
                  key={s.nombre}
                  className={`p-3 rounded-lg border ${colores.bg}`}
                  data-testid={`semaforo-${s.nombre.toLowerCase().replace(/ /g, '-')}`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`w-3 h-3 rounded-full ${colores.dot} ${s.estado === 'rojo' ? 'animate-pulse' : ''}`} />
                    <span className="text-xs font-semibold text-gray-700 uppercase tracking-wide">{s.nombre}</span>
                  </div>
                  <div className={`text-xl font-bold mono ${colores.text}`}>{s.valor}</div>
                  <p className="text-xs text-gray-500 mt-1 leading-snug">{s.explicacion}</p>
                  {s.composicion && (
                    <p className="text-[10px] font-mono text-gray-400 mt-1.5 pt-1.5 border-t border-gray-200/70 leading-snug">
                      {s.composicion}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Flujo de Efectivo - {displayChartData.length} Semanas</CardTitle>
            <CardDescription>Ingresos, egresos y saldo acumulado</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[280px]">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={displayChartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="semana" tick={{fontSize: 11}} stroke="#94A3B8" />
                  <YAxis tick={{fontSize: 11}} stroke="#94A3B8" tickFormatter={(v) => `${(v/1000).toFixed(0)}k`} />
                  <Tooltip 
                    formatter={(value, name) => [formatCurrency(value), name]}
                    contentStyle={{backgroundColor: '#fff', border: '1px solid #E2E8F0', borderRadius: '8px'}}
                  />
                  <Legend />
                  <Bar dataKey="ingresos" name="Ingresos" fill="#10B981" radius={[2, 2, 0, 0]} />
                  <Bar dataKey="egresos" name="Egresos" fill="#EF4444" radius={[2, 2, 0, 0]} />
                  <Line type="monotone" dataKey="saldo_final" name="Saldo" stroke="#6366F1" strokeWidth={2} dot={{ fill: '#6366F1', r: 3 }} />
                  <ReferenceLine y={0} stroke="#94A3B8" strokeDasharray="3 3" />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Varianza Semanal</CardTitle>
            <CardDescription>Cambio vs semana anterior</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-[280px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={displayChartData.slice(1)}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="semana" tick={{fontSize: 11}} stroke="#94A3B8" />
                  <YAxis tick={{fontSize: 11}} stroke="#94A3B8" tickFormatter={(v) => `${(v/1000).toFixed(0)}k`} />
                  <Tooltip 
                    formatter={(value, name) => [formatCurrency(value), 'Varianza']}
                    contentStyle={{backgroundColor: '#fff', border: '1px solid #E2E8F0', borderRadius: '8px'}}
                  />
                  <ReferenceLine y={0} stroke="#94A3B8" />
                  <Bar dataKey="varianza" name="Varianza" fill={(entry) => entry.varianza >= 0 ? '#10B981' : '#EF4444'}>
                    {displayChartData.slice(1).map((entry, index) => (
                      <rect key={index} fill={entry.varianza >= 0 ? '#10B981' : '#EF4444'} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Cash Pooling & Account Details */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Layers size={16} className="text-[#6366F1]" />
              Cash Pooling por Moneda
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {Object.entries(cashPool).map(([currency, data]) => (
                <div key={currency} className="flex items-center justify-between p-3 bg-[#F1F5F9] rounded-lg">
                  <div>
                    <div className="font-semibold text-[#0F172A]">{currency}</div>
                    <div className="text-xs text-[#64748B]">{data.cuentas} cuenta(s)</div>
                  </div>
                  <div className="text-right">
                    <div className="font-bold mono text-[#0F172A]">
                      ${data.total.toLocaleString('es-MX', {minimumFractionDigits: 2})}
                    </div>
                  </div>
                </div>
              ))}
              {Object.keys(cashPool).length === 0 && (
                <p className="text-sm text-[#94A3B8] text-center py-4">Sin datos de cash pooling</p>
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-sm lg:col-span-2">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Building2 size={16} className="text-[#0EA5E9]" />
              Detalle de Cuentas
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 max-h-[250px] overflow-y-auto">
              {accounts.map((acc) => (
                <div key={acc.id} className={`flex items-center justify-between p-3 rounded-lg border ${
                  acc.riesgo_ocioso ? 'border-amber-300 bg-amber-50' : 
                  acc.riesgo_bajo_saldo ? 'border-red-300 bg-red-50' : 
                  'border-[#E2E8F0] bg-white'
                }`}>
                  <div className="flex items-center gap-3">
                    <div className={`w-2 h-8 rounded-full ${
                      acc.riesgo === 'alto' ? 'bg-red-400' : 
                      acc.riesgo === 'medio' ? 'bg-amber-400' : 
                      'bg-green-400'
                    }`} />
                    <div>
                      <div className="font-medium text-[#0F172A]">{acc.nombre}</div>
                      <div className="text-xs text-[#64748B]">{acc.banco} · {acc.moneda}</div>
                      {acc.num_movimientos > 0 && (
                        <div className="text-xs text-blue-600">{acc.num_movimientos} movimientos</div>
                      )}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-xs text-[#64748B]">Saldo Final</div>
                    <div className="font-bold mono text-lg">
                      ${acc.saldo_final?.toLocaleString('es-MX', {minimumFractionDigits: 2})} {acc.moneda}
                    </div>
                    {acc.moneda !== 'MXN' && (
                      <div className="text-xs text-[#64748B]">
                        ≈ ${acc.saldo_final_mxn?.toLocaleString('es-MX', {minimumFractionDigits: 2})} MXN
                      </div>
                    )}
                    {acc.saldo_inicial !== acc.saldo_final && (
                      <div className="text-xs mt-1">
                        <span className="text-[#64748B]">Inicial: </span>
                        <span className="mono">${acc.saldo_inicial?.toLocaleString('es-MX', {minimumFractionDigits: 2})}</span>
                        <span className={`ml-1 ${(acc.saldo_final - acc.saldo_inicial) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {(acc.saldo_final - acc.saldo_inicial) >= 0 ? '+' : ''}
                          ${(acc.saldo_final - acc.saldo_inicial)?.toLocaleString('es-MX', {minimumFractionDigits: 2})}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
              {accounts.length === 0 && (
                <p className="text-sm text-[#94A3B8] text-center py-4">No hay cuentas bancarias</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Diagnóstico y Acciones */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="shadow-sm border-t-4 border-t-blue-500">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Wallet size={16} className="text-blue-600" />
              KPIs Clave de Liquidez
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex justify-between items-center p-3 bg-blue-50 rounded-lg">
                <div>
                  <div className="text-xs text-blue-700 font-medium">RUNWAY</div>
                  <div className="text-xs text-gray-500">Semanas de operación con saldo actual</div>
                </div>
                <div className="text-right">
                  {(() => {
                    // RUNWAY = saldo actual real / burn rate semanal promedio
                    // Saldo actual real = saldo final de la ÚLTIMA semana con datos reales
                    // (is_past/is_current — no proyectadas). Burn rate = egresos promedio
                    // de esas mismas semanas reales.
                    const semanasReales = chartData.filter(w => (w.es_real || w.es_current));
                    const saldoActual = semanasReales.length > 0
                      ? semanasReales[semanasReales.length - 1].saldo_final
                      : saldoInicial; // fallback si la ventana no incluye semanas reales
                    const burnRateSemanal = semanasReales.length > 0
                      ? semanasReales.reduce((sum, w) => sum + (w.egresos || 0), 0) / semanasReales.length
                      : 0;
                    const runway = burnRateSemanal > 0 ? Math.max(0, saldoActual) / burnRateSemanal : null;
                    return (
                      <>
                        <span className={`text-2xl font-bold ${
                          runway === null ? 'text-gray-400' :
                          runway > 13 ? 'text-green-600' :
                          runway > 6 ? 'text-amber-600' : 'text-red-600'
                        }`}>
                          {runway === null ? '—' : Math.round(runway)}
                        </span>
                        <span className="text-sm text-gray-500 ml-1">semanas</span>
                        {runway === null && (
                          <div className="text-xs text-gray-400">sin egresos en el período</div>
                        )}
                      </>
                    );
                  })()}
                </div>
              </div>
              <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                <div>
                  <div className="text-xs text-gray-700 font-medium">BURN RATE</div>
                  <div className="text-xs text-gray-500">Promedio de egresos semanales</div>
                </div>
                <div className="text-right">
                  {(() => {
                    // Mismo cálculo que usa RUNWAY: egresos promedio de las semanas
                    // con datos reales (is_past/is_current), sin diluir por futuras vacías
                    const semanasReales = chartData.filter(w => (w.es_real || w.es_current));
                    const burnRateSemanal = semanasReales.length > 0
                      ? semanasReales.reduce((sum, w) => sum + (w.egresos || 0), 0) / semanasReales.length
                      : 0;
                    return (
                      <>
                        <span className="text-lg font-bold text-red-600">
                          {formatCurrency(burnRateSemanal)}
                        </span>
                        <span className="text-xs text-gray-500 ml-1">/sem</span>
                      </>
                    );
                  })()}
                </div>
              </div>
              <div className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                <div>
                  <div className="text-xs text-gray-700 font-medium">COBRANZA VS PAGOS</div>
                  <div className="text-xs text-gray-500">Ratio ingresos / egresos</div>
                </div>
                <div className="text-right">
                  {(() => {
                    const totalIng = chartData.reduce((sum, w) => sum + (w.ingresos || 0), 0);
                    const totalEgr = chartData.reduce((sum, w) => sum + (w.egresos || 0), 0);
                    const ratio = totalEgr > 0 ? (totalIng / totalEgr) : 1;
                    return (
                      <span className={`text-lg font-bold ${ratio >= 1 ? 'text-green-600' : 'text-red-600'}`}>
                        {(ratio * 100).toFixed(0)}%
                      </span>
                    );
                  })()}
                </div>
              </div>
              <div className="flex justify-between items-center p-3 bg-amber-50 rounded-lg">
                <div>
                  <div className="text-xs text-amber-700 font-medium">SEMANA CRÍTICA</div>
                  <div className="text-xs text-gray-500">Menor saldo proyectado</div>
                </div>
                <div className="text-right">
                  {(() => {
                    // Solo semanas futuras (proyectadas) — mismo criterio que ¿Qué Hacer Ahora?
                    const minWeek = chartData
                      .filter(w => !(w.es_real || w.es_current))
                      .reduce((min, w, i) =>
                        (!min || w.saldo_final < min.saldo_final) ? {...w, idx: i} : min, null);
                    return minWeek ? (
                      <>
                        <span className={`text-lg font-bold ${minWeek.saldo_final < 0 ? 'text-red-600' : 'text-amber-600'}`}>
                          {minWeek.semana}
                        </span>
                        <div className="text-xs text-gray-500">{formatCurrency(minWeek.saldo_final)}</div>
                      </>
                    ) : '-';
                  })()}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-sm border-t-4 border-t-green-500">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <CheckCircle2 size={16} className="text-green-600" />
              ¿Qué Hacer Ahora?
            </CardTitle>
            <CardDescription>Acciones recomendadas basadas en tu flujo</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {(() => {
                const recommendations = [];
                // Semana crítica = la semana FUTURA (proyectada) con menor saldo;
                // las pasadas ya ocurrieron y no son accionables
                const semanasFuturas = chartData.filter(w => !(w.es_real || w.es_current));
                const minWeek = semanasFuturas.reduce((min, w) =>
                  (!min || w.saldo_final < min.saldo_final) ? w : min, null);

                // Cobranza unificada: déficit proyectado + cartera vencida del Aging en
                // UNA sola tarjeta (antes eran dos con la misma lista de clientes).
                // % vencido se muestra como badge junto al título.
                const hayDeficit = risks.liquidez_critica || (minWeek && minWeek.saldo_final < 0);
                const carteraVencida = (agingCxc.pctVencido ?? 0) > 30;
                if (hayDeficit || carteraVencida) {
                  const detalles = [];
                  if (hayDeficit && minWeek) {
                    const rango = formatWeekRange(minWeek);
                    detalles.push(
                      `Semana ${minWeek.semana}${rango ? ` (${rango})` : ''} proyecta déficit de ` +
                      `${formatCurrency(Math.abs(minWeek.saldo_final || 0))} — considera línea de crédito`
                    );
                  }
                  if (carteraVencida) {
                    detalles.push(
                      `Cartera CxC vencida: $${agingCxc.vencido.toLocaleString('es-MX', {maximumFractionDigits: 0})} de ` +
                      `$${agingCxc.bruto.toLocaleString('es-MX', {maximumFractionDigits: 0})} MXN brutos`
                    );
                  }
                  recommendations.push({
                    priority: (hayDeficit || agingCxc.pctVencido > 50) ? 'alta' : 'media',
                    icon: '🚨',
                    text: 'Acelerar gestión de cobranza',
                    pctVencido: carteraVencida ? agingCxc.pctVencido : null, // badge junto al título
                    detalles,
                    clientes: topClientesCxc // top deudores del Aging CxC con nombre y monto
                  });
                }
                if (risks.saldos_ociosos > 0) {
                  recommendations.push({
                    priority: 'media', icon: '💰',
                    text: 'Invertir saldos excedentes',
                    detail: `${risks.saldos_ociosos} cuenta(s) con más de $500K sin rendimiento`
                  });
                }
                if (trend.direction === 'up' && trend.avg_flow_4w > 0) {
                  recommendations.push({
                    priority: 'baja', icon: '📈',
                    text: 'Aprovechar tendencia positiva',
                    detail: `Flujo promedio +${formatCurrency(trend.avg_flow_4w)}/semana`
                  });
                }
                if (kpis.total_cfdis > kpis.total_reconciliations) {
                  recommendations.push({
                    priority: 'media', icon: '📋',
                    text: 'Conciliar CFDIs pendientes',
                    detail: `${kpis.total_cfdis - kpis.total_reconciliations} facturas sin conciliar`
                  });
                }
                if (recommendations.length === 0) {
                  recommendations.push({
                    priority: 'baja', icon: '✅',
                    text: 'Flujo de efectivo saludable',
                    detail: 'Continuar con el monitoreo semanal'
                  });
                }
                return recommendations.map((rec, idx) => (
                  <div key={idx} className={`flex items-start gap-3 p-3 rounded-lg ${
                    rec.priority === 'alta' ? 'bg-red-50 border border-red-200' :
                    rec.priority === 'media' ? 'bg-amber-50 border border-amber-200' :
                    'bg-green-50 border border-green-200'
                  }`}>
                    <span className="text-xl">{rec.icon}</span>
                    <div className="flex-1 min-w-0">
                      <div className={`font-medium text-sm flex items-center gap-2 flex-wrap ${
                        rec.priority === 'alta' ? 'text-red-800' :
                        rec.priority === 'media' ? 'text-amber-800' :
                        'text-green-800'
                      }`}>
                        {rec.text}
                        {rec.pctVencido != null && (
                          <span className="text-[11px] font-mono font-bold px-1.5 py-0.5 rounded bg-red-100 text-red-700">
                            {rec.pctVencido.toFixed(0)}% vencido
                          </span>
                        )}
                      </div>
                      {rec.detail && <div className="text-xs text-gray-600">{rec.detail}</div>}
                      {rec.detalles && rec.detalles.map((d, i) => (
                        <div key={i} className="text-xs text-gray-600">{d}</div>
                      ))}
                      {rec.clientes && rec.clientes.length > 0 && (
                        <div className="mt-2 space-y-1" data-testid="top-clientes-cobranza">
                          <div className="text-[11px] font-semibold text-gray-500 uppercase">
                            Clientes con mayor saldo pendiente (Aging CxC)
                          </div>
                          {rec.clientes.map((c, i) => (
                            <div key={i} className="flex justify-between items-center gap-2 text-xs bg-white/70 rounded px-2 py-1">
                              <span className="truncate" title={c.nombre}>{c.nombre}</span>
                              <span className="font-mono font-semibold whitespace-nowrap">
                                ${c.monto.toLocaleString('es-MX', {minimumFractionDigits: 2})} MXN
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ));
              })()}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Escenarios de Liquidez */}
      <Card className="shadow-sm">
        <CardHeader className="pb-2">
          <div className="flex justify-between items-center">
            <div>
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <AlertTriangle size={16} className="text-amber-600" />
                Análisis de Escenarios
              </CardTitle>
              <CardDescription>Proyección de liquidez bajo diferentes condiciones</CardDescription>
            </div>
            <Dialog open={scenarioDialogOpen} onOpenChange={(open) => {
              setScenarioDialogOpen(open);
              if (open) setTempScenarioConfig(scenarioConfig);
            }}>
              <DialogTrigger asChild>
                <Button variant="outline" size="sm" className="gap-2">
                  <Settings size={14} />
                  Configurar
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle>Configurar Escenarios</DialogTitle>
                </DialogHeader>
                <div className="space-y-6 py-4">
                  <div className="space-y-3 p-4 bg-red-50 rounded-lg border border-red-200">
                    <div className="flex items-center gap-2">
                      <TrendingDown size={16} className="text-red-600" />
                      <span className="font-medium text-red-800">Escenario Pesimista</span>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-1">
                        <Label className="text-xs">Variación Cobranza (%)</Label>
                        <Input type="number" value={tempScenarioConfig.pesimista.cobranza}
                          onChange={(e) => setTempScenarioConfig({...tempScenarioConfig, pesimista: {...tempScenarioConfig.pesimista, cobranza: parseFloat(e.target.value) || 0}})}
                          className="h-8" />
                        <p className="text-xs text-gray-500">Negativo = menos cobranza</p>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Variación Gastos (%)</Label>
                        <Input type="number" value={tempScenarioConfig.pesimista.gastos}
                          onChange={(e) => setTempScenarioConfig({...tempScenarioConfig, pesimista: {...tempScenarioConfig.pesimista, gastos: parseFloat(e.target.value) || 0}})}
                          className="h-8" />
                        <p className="text-xs text-gray-500">Positivo = más gastos</p>
                      </div>
                    </div>
                  </div>
                  <div className="space-y-3 p-4 bg-green-50 rounded-lg border border-green-200">
                    <div className="flex items-center gap-2">
                      <TrendingUp size={16} className="text-green-600" />
                      <span className="font-medium text-green-800">Escenario Optimista</span>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-1">
                        <Label className="text-xs">Variación Cobranza (%)</Label>
                        <Input type="number" value={tempScenarioConfig.optimista.cobranza}
                          onChange={(e) => setTempScenarioConfig({...tempScenarioConfig, optimista: {...tempScenarioConfig.optimista, cobranza: parseFloat(e.target.value) || 0}})}
                          className="h-8" />
                        <p className="text-xs text-gray-500">Positivo = más cobranza</p>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Variación Gastos (%)</Label>
                        <Input type="number" value={tempScenarioConfig.optimista.gastos}
                          onChange={(e) => setTempScenarioConfig({...tempScenarioConfig, optimista: {...tempScenarioConfig.optimista, gastos: parseFloat(e.target.value) || 0}})}
                          className="h-8" />
                        <p className="text-xs text-gray-500">Negativo = menos gastos</p>
                      </div>
                    </div>
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setScenarioDialogOpen(false)}>Cancelar</Button>
                  <Button onClick={handleSaveScenarios} className="bg-[#0F172A]">Guardar</Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-4 bg-red-50 rounded-lg border border-red-200">
              <div className="flex items-center gap-2 mb-2">
                <TrendingDown size={16} className="text-red-600" />
                <span className="font-medium text-red-800">Escenario Pesimista</span>
              </div>
              <p className="text-xs text-gray-600 mb-3">
                Cobranza {scenarioConfig.pesimista.cobranza >= 0 ? '+' : ''}{scenarioConfig.pesimista.cobranza}%, 
                gastos {scenarioConfig.pesimista.gastos >= 0 ? '+' : ''}{scenarioConfig.pesimista.gastos}%
              </p>
              {(() => {
                const avgIng = chartData.reduce((sum, w) => sum + (w.ingresos || 0), 0) / Math.max(chartData.length, 1);
                const avgEgr = chartData.reduce((sum, w) => sum + (w.egresos || 0), 0) / Math.max(chartData.length, 1);
                const pesimista = saldoInicial + ((avgIng * (1 + scenarioConfig.pesimista.cobranza/100)) - (avgEgr * (1 + scenarioConfig.pesimista.gastos/100))) * 13;
                return (
                  <div className={`text-xl font-bold ${pesimista < 0 ? 'text-red-600' : 'text-gray-800'}`}>
                    {formatCurrency(pesimista)}
                    {pesimista < 0 && <span className="text-xs ml-2 text-red-500">⚠️ Déficit</span>}
                  </div>
                );
              })()}
              <p className="text-xs text-gray-500 mt-1">Saldo al final de S13</p>
            </div>
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex items-center gap-2 mb-2">
                <Minus size={16} className="text-blue-600" />
                <span className="font-medium text-blue-800">Escenario Base</span>
              </div>
              <p className="text-xs text-gray-600 mb-3">Tendencia actual continúa</p>
              <div className={`text-xl font-bold ${saldoFinalProyectado < 0 ? 'text-red-600' : 'text-blue-800'}`}>
                {formatCurrency(saldoFinalProyectado)}
              </div>
              <p className="text-xs text-gray-500 mt-1">Saldo al final de S13</p>
            </div>
            <div className="p-4 bg-green-50 rounded-lg border border-green-200">
              <div className="flex items-center gap-2 mb-2">
                <TrendingUp size={16} className="text-green-600" />
                <span className="font-medium text-green-800">Escenario Optimista</span>
              </div>
              <p className="text-xs text-gray-600 mb-3">
                Cobranza {scenarioConfig.optimista.cobranza >= 0 ? '+' : ''}{scenarioConfig.optimista.cobranza}%, 
                gastos {scenarioConfig.optimista.gastos >= 0 ? '+' : ''}{scenarioConfig.optimista.gastos}%
              </p>
              {(() => {
                const avgIng = chartData.reduce((sum, w) => sum + (w.ingresos || 0), 0) / Math.max(chartData.length, 1);
                const avgEgr = chartData.reduce((sum, w) => sum + (w.egresos || 0), 0) / Math.max(chartData.length, 1);
                const optimista = saldoInicial + ((avgIng * (1 + scenarioConfig.optimista.cobranza/100)) - (avgEgr * (1 + scenarioConfig.optimista.gastos/100))) * 13;
                return <div className="text-xl font-bold text-green-800">{formatCurrency(optimista)}</div>;
              })()}
              <p className="text-xs text-gray-500 mt-1">Saldo al final de S13</p>
            </div>
          </div>
        </CardContent>
      </Card>


    </div>
  );
};

export default Dashboard;
